import { cloneDeep } from 'lodash-es';
import { AppAlertItem } from '@/models/appAlert';
import { createAppAlertPageHeaders } from '@/pages/appAlerts/AppAlertPageHeaders';
import { appAlertService } from '@/services/appAlertService';
import { isRequestCanceled } from '@/services/baseService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IAppAlertFilters, IPageOptions, IPageSortOption } from '@/types';
import type { DataTableHeader } from '@/types/vuetify';

const prioritySort = { key: 'priority', order: 'asc' as const };
const defaultSortBy = [{ key: 'created_at', order: 'desc' as const }];

function getAppAlertSortByForApi(sortBy: IPageSortOption[]): IPageSortOption[] {
    const userSort = sortBy.filter((s) => s.key !== 'priority');
    return [prioritySort, ...(userSort.length > 0 ? userSort : defaultSortBy)];
}

function createAppAlertStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<AppAlertItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<IAppAlertFilters>({
        alertDate: {
            value: null as [Date, Date] | null,
            title: 'appAlerts.alertDateFilter',
            allowSaveState: false,
            disableClear: false,
        },
        alertNameId: {
            value: null as number[] | null,
            title: 'appAlerts.alertCode',
            allowSaveState: true,
        },
        alertTypeId: {
            value: null as string[] | null,
            title: 'appAlerts.alertType',
            allowSaveState: true,
        },
        alertPriorityId: {
            value: null as string[] | null,
            title: 'appAlerts.priority',
            allowSaveState: true,
        },
        alertIsDismissed: {
            value: '0',
            title: 'appAlerts.dismissed',
            allowSaveState: true,
            disableClear: true,
        },
        alertText: {
            value: null,
            title: 'appAlerts.text',
            allowSaveState: true,
        },
        alertBookmakerId: {
            value: null,
            title: 'common.allBookmakers',
            allowSaveState: true,
        },
        alertIsInvestigate: {
            value: null,
            title: 'appAlerts.investigate',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        filters.alertDate.value = null;
        filters.alertNameId.value = null as number[] | null;
        filters.alertTypeId.value = null as string[] | null;
        filters.alertPriorityId.value = null as string[] | null;
        filters.alertIsDismissed.value = '0';
        filters.alertText.value = null;
        filters.alertBookmakerId.value = null;
        filters.alertIsInvestigate.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createAppAlertPageHeaders());

    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'appAlert',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const filtersToSend = cloneDeep(filters);

            const params = {
                filters: filtersToSend,
                options: {
                    sortBy: getAppAlertSortByForApi(options.value.sortBy),
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['AlertName'],
            };

            const response = await appAlertService.list(params);

            items.value = (response.data.rows || []).map((row) => new AppAlertItem(row));
            total.value = response.data.total ?? 0;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        stats: undefined,
    };
}

export function useAppAlertStore(storeId: string) {
    const useStore = defineStore(storeId, () => createAppAlertStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'appAlert', defaultSortBy),
    });
    return useStore();
}
